import { ProductionRoutePeriodCoverage } from '@/hooks/api/v2/productionRoutes'
import { useCreateReport } from '@/hooks/api/v2/reports'
import useI18n from '@/shared/hooks/useI18n'
import { Button, DisabledMonth, GenericDialog, MonthRangePicker } from '@/common'
import { CalendarEmptyIcon } from '@/common/components/icons'
import { format } from 'date-fns'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

function getMonthsFromCoverage(monthsCoverage: ProductionRoutePeriodCoverage[]): {
  date: Date
}[] {
  return monthsCoverage.map(month => ({
    date: new Date(month.month)
  }))
}

function getFutureMonths(monthsCoverage: ProductionRoutePeriodCoverage[]): DisabledMonth[] {
  return monthsCoverage
    .filter(month => !month.reportingEnabled && new Date(month.month) > new Date())
    .map(month => ({
      date: new Date(month.month),
      reason: 'future' as const
    }))
}

function getReportedMonths(monthsCoverage: ProductionRoutePeriodCoverage[]): DisabledMonth[] {
  return monthsCoverage
    .filter(month => month.reportSubmitted || month.reportDraft)
    .map(month => ({
      date: new Date(month.month),
      reason: 'reported' as const
    }))
}

function getOutOfSpanMonths(startDate: Date, endDate: Date): DisabledMonth[] {
  const calendarMonths: Date[] = []
  const startYear = startDate.getFullYear()
  const endYear = endDate.getFullYear()

  for (let year = startYear - 1; year <= endYear + 1; year++) {
    for (let month = 0; month < 12; month++) {
      calendarMonths.push(new Date(year, month, 1))
    }
  }

  return calendarMonths
    .filter(monthDate => {
      const monthYear = monthDate.getFullYear()
      const monthMonth = monthDate.getMonth()

      const startYear = startDate.getFullYear()
      const startMonth = startDate.getMonth()
      const endYear = endDate.getFullYear()
      const endMonth = endDate.getMonth()

      return (
        monthYear < startYear ||
        (monthYear === startYear && monthMonth < startMonth) ||
        monthYear > endYear ||
        (monthYear === endYear && monthMonth > endMonth)
      )
    })
    .map(monthDate => ({
      date: monthDate,
      reason: 'outOfSpan' as const
    }))
}

function combineDisabledMonths(
  futureMonths: DisabledMonth[],
  reportedMonths: DisabledMonth[],
  outOfSpanMonths: DisabledMonth[]
): DisabledMonth[] {
  return [...futureMonths, ...reportedMonths, ...outOfSpanMonths]
}

export default function CreateReportDialog({
  isOpen,
  close,
  productionRouteName,
  periodLabel,
  monthsCoverage,
  startDate,
  endDate,
  installationId,
  productionRouteId
}: {
  isOpen: boolean
  close: () => void
  productionRouteName: string
  periodLabel: string
  monthsCoverage: ProductionRoutePeriodCoverage[]
  startDate: Date
  endDate: Date
  installationId: string
  productionRouteId: string
}) {
  const { t, currentLanguage } = useI18n()
  const navigate = useNavigate()

  const { mutateAsync: createReport, isPending } = useCreateReport(installationId)
  const [selectedRange, setSelectedRange] = useState<{ start: Date | null; end: Date | null }>({
    start: null,
    end: null
  })

  const formattedRangeForPayload = useMemo(() => {
    if (!selectedRange.start || !selectedRange.end) {
      return null
    }

    return {
      startDate: format(selectedRange.start, 'yyyy-MM-dd'),
      endDate: format(selectedRange.end, 'yyyy-MM-dd')
    }
  }, [selectedRange])

  const onAddClick = async () => {
    if (!formattedRangeForPayload) {
      return
    }

    try {
      const report = await createReport({
        productionRouteId,
        startDate: formattedRangeForPayload.startDate,
        endDate: formattedRangeForPayload.endDate
      })

      onClose()
      navigate(`./view/${report.id}`)
    } catch (error) {
      console.error('Error creating report', error)
    }
  }

  const onClose = () => {
    setSelectedRange({ start: null, end: null })
    close()
  }

  const onMonthChange = (range: { start: Date | null; end: Date | null }) => {
    setSelectedRange(range)
  }

  // Use the pure functions with useMemo
  const months = useMemo(() => getMonthsFromCoverage(monthsCoverage), [monthsCoverage])
  const futureMonths = useMemo(() => getFutureMonths(monthsCoverage), [monthsCoverage])
  const reportedMonths = useMemo(() => getReportedMonths(monthsCoverage), [monthsCoverage])
  const outOfSpanMonths = useMemo(
    () => getOutOfSpanMonths(startDate, endDate),
    [startDate, endDate]
  )
  const disabledMonths = useMemo(
    () => combineDisabledMonths(futureMonths, reportedMonths, outOfSpanMonths),
    [futureMonths, reportedMonths, outOfSpanMonths]
  )

  const isCreateButtonDisabled = !selectedRange.end || !selectedRange.start

  return (
    <GenericDialog
      title={`${productionRouteName} - ${periodLabel}`}
      subtitle={t('reports:createReportDialog.subtitle')}
      icon={<CalendarEmptyIcon className="size-6 text-green-500" />}
      isOpen={isOpen}
      close={close}
      size="2xl"
    >
      <div className="space-y-4">
        <div className="flex justify-center">
          <MonthRangePicker
            onChange={onMonthChange}
            months={months}
            placeholderHeader={t('reports:createReportDialog.monthRangePicker.placeholderHeader')}
            locale={currentLanguage as string}
            disabledMonths={disabledMonths}
            tooltips={{
              outOfSpan: t('reports:createReportDialog.monthRangePicker.outOfSpanTooltip'),
              future: t('reports:createReportDialog.monthRangePicker.futureTooltip'),
              reported: t('reports:createReportDialog.monthRangePicker.reportedTooltip')
            }}
          />
        </div>
        <footer className="gap-2 flex justify-end">
          <Button variant="neutral" onClick={close}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={onAddClick} isLoading={isPending} disabled={isCreateButtonDisabled}>
            {t('reports:createReportDialog.createReportButton')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
